import { fabric } from 'fabric';
// eslint-disable-next-line no-restricted-imports -- fabric.Control.render needs an HTMLImageElement URL
import DeleteSvg from '@assets/icons/ShapesDrawingTools/delete.svg';

type ChangeControlPositionProps = {
  obj: any;
  angle: number;
};

type ControlPosition = {
  x: number;
  y: number;
  offsetX: number;
  offsetY: number;
};

const ICON_OFFSET = 18;
const EDGE_MARGIN = 14;

const img = new Image();
img.src = DeleteSvg.toString();
img.style.objectFit = 'cover';
export default function changeObjectControlPosition({
  obj,
  angle
}: ChangeControlPositionProps) {
  const options = getEdgeAwareControlPosition(obj, angle);
  obj.controls.deleteControl = new fabric.Control({
    ...options,
    cursorStyle: 'pointer',
    positionHandler: clampedPositionHandler,
    mouseUpHandler: (_, transformData) => deleteObjectHandler(transformData),
    render: (ctx, top, left) => renderIcon(ctx, top, left)
  });
}

function clampedPositionHandler(
  this: fabric.Control,
  dim: any,
  finalMatrix: any,
  fabricObject: any,
  currentControl: any
) {
  const point = (fabric.Control.prototype as any).positionHandler.call(
    this,
    dim,
    finalMatrix,
    fabricObject,
    currentControl
  );
  const canvas = fabricObject?.canvas;
  if (!canvas) return point;
  const maxX = canvas.getWidth() - EDGE_MARGIN;
  const maxY = canvas.getHeight() - EDGE_MARGIN;
  return new fabric.Point(
    Math.min(Math.max(point.x, EDGE_MARGIN), maxX),
    Math.min(Math.max(point.y, EDGE_MARGIN), maxY)
  );
}

function setControllerPosition(angle: number): ControlPosition {
  if (angle === 90)
    return {
      x: -0.5,
      y: -0.5,
      offsetY: -ICON_OFFSET,
      offsetX: -ICON_OFFSET
    };

  if (angle === 180)
    return {
      x: -0.5,
      y: 0.5,
      offsetY: ICON_OFFSET,
      offsetX: -ICON_OFFSET
    };

  if (angle === 270)
    return {
      x: 0.5,
      y: 0.5,
      offsetY: ICON_OFFSET,
      offsetX: ICON_OFFSET
    };

  return {
    x: 0.5,
    y: -0.5,
    offsetY: -ICON_OFFSET,
    offsetX: ICON_OFFSET
  };
}

function getEdgeAwareControlPosition(obj: any, angle: number): ControlPosition {
  const base = setControllerPosition(angle);
  const canvas = obj?.canvas;
  if (!canvas || typeof obj.getBoundingRect !== 'function') return base;

  const rect = obj.getBoundingRect(true, true);
  const canvasWidth = canvas.getWidth();
  const canvasHeight = canvas.getHeight();

  let { x, y, offsetX, offsetY } = base;

  const iconCenterRight = rect.left + rect.width + Math.abs(offsetX);
  const iconCenterLeft = rect.left - Math.abs(offsetX);
  if (offsetX > 0) {
    if (
      iconCenterRight + EDGE_MARGIN > canvasWidth &&
      iconCenterLeft - EDGE_MARGIN >= 0
    ) {
      x = -x;
      offsetX = -offsetX;
    }
  } else if (
    iconCenterLeft - EDGE_MARGIN < 0 &&
    iconCenterRight + EDGE_MARGIN <= canvasWidth
  ) {
    x = -x;
    offsetX = -offsetX;
  }

  const iconCenterTop = rect.top - Math.abs(offsetY);
  const iconCenterBottom = rect.top + rect.height + Math.abs(offsetY);
  if (offsetY < 0) {
    if (
      iconCenterTop - EDGE_MARGIN < 0 &&
      iconCenterBottom + EDGE_MARGIN <= canvasHeight
    ) {
      y = -y;
      offsetY = -offsetY;
    }
  } else if (
    iconCenterBottom + EDGE_MARGIN > canvasHeight &&
    iconCenterTop - EDGE_MARGIN >= 0
  ) {
    y = -y;
    offsetY = -offsetY;
  }

  return { x, y, offsetX, offsetY };
}

function deleteObjectHandler(transformData: fabric.Transform) {
  const { target } = transformData;
  const { canvas } = target;
  if (!canvas) return false;
  canvas.discardActiveObject();
  canvas.remove(target);
  canvas.renderAll();
  canvas.fire('object:deleted', { target });
  return true;
}

function renderIcon(ctx: CanvasRenderingContext2D, left: number, top: number) {
  ctx.save();
  ctx.translate(left, top);
  ctx.rotate(fabric.util.degreesToRadians(0));
  ctx.drawImage(img, -12, -14, 24, 24);
  ctx.restore();
}
